import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { forkJoin, merge, of, Subject, Subscription } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { ConfigService } from 'src/app/service/config.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import {
  AiGeneratedQueryData,
  AiSearchService,
  truncateDescription,
} from 'src/app/shared/services/ai-search/ai-search.service';
import { ReportStateService } from 'src/app/shared/services/report-state.service';
import { SentimentService } from 'src/app/shared/services/data-api/sentiment.service';
import { SubEventsService } from 'src/app/shared/services/data-api/sub-events.service';
import { VolumeTimeSeriesService } from 'src/app/shared/services/data-api/volume-time-series.service';
import { DdSummaryService } from 'src/app/shared/services/llm/dd-summary.service';
import { CompanyFormComponent } from 'src/app/cockpit/main-menu/menu-search/ai-search-dialog/company-form/company-form.component';
import {
  CompanyFormData,
  CompanyQueryRequest,
  Lexicon,
  LexiconResponse,
  YukkaQuery,
} from 'src/app/shared/models/ai-search.interface';
import { AiLoadingPhase } from 'src/app/micro-components/ai-loading-card/ai-loading-card.component';
import { DueDiligenceRecentsService } from './due-diligence-recents.service';
import {
  DD_TIMEFRAME,
  DD_TIMEFRAME_LABEL,
  DD_TOP_EVENTS_SHOWN,
  DdEditableField,
  DdListField,
  DdPageState,
  DdResultTiles,
  DdTileDeltas,
  EMPTY_DD_TILES,
  RecentDdSearch,
} from './due-diligence.model';
import {
  QbCounts,
  QbModel,
  cloneTree,
  deriveBuilderModel,
  hasIncomplete,
  queryCounts,
} from 'src/app/micro-components/query-builder/query-builder.model';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { QueryNameComponent } from 'src/app/cockpit/main-news/news-query/query-name/query-name.component';
import { TermField } from 'src/app/micro-components/term-picker/term-picker.component';

@Component({
  selector: 'app-due-diligence',
  templateUrl: './due-diligence.component.html',
  styleUrls: ['./due-diligence.component.scss'],
})
export class DueDiligenceComponent implements OnInit, OnDestroy {
  state: DdPageState = 'search';
  loadingPhase: AiLoadingPhase = 'enriching';
  loadingSubject = '';
  errorMessage = '';
  companySearchEnabled = false;
  // "Why it matters" expander for the legal-name guidance box.
  whyOpen = false;

  userEmail = '';
  project = '';
  recents: RecentDdSearch[] = [];

  resultData: AiGeneratedQueryData | null = null;
  // "Search parameters" panel (results page): the company inputs + lexicon and
  // the generated boolean query, toggled open below the action bar.
  paramsOpen = false;
  // The request that produced the query — displayed in the panel, edited on
  // re-run, persisted with recents. Also the generate/company body verbatim.
  request: CompanyQueryRequest | null = null;
  // The lexicon that produced the query — drives verified volumes and the
  // add-term suggestions. Persisted with recents.
  lexicon: Lexicon | null = null;
  // View-mode rows, derived from the committed query tree (resultData.query).
  // In flight to generate/company; promoted to `request` once it answers.
  private pendingRequest: CompanyQueryRequest | null = null;

  // Edit & re-run state. `dQuery` is a deep-cloned draft tree; it commits to
  // resultData.query only when the re-run fires, and discards on cancel.
  editing = false;
  dQuery: YukkaQuery | null = null;
  draftModel: QbModel | null = null;
  draftRequest: CompanyQueryRequest | null = null;
  // The open add-term picker (keyed by target node path) and its input.
  addKey: string | null = null;
  addDraft = '';
  rerunning = false;
  rerunAt: number | null = null;
  deltas: DdTileDeltas | null = null;
  readonly timeframe = DD_TIMEFRAME;
  sectorOptions: string[] = [];
  private prevTiles: DdResultTiles | null = null;

  tiles: DdResultTiles = EMPTY_DD_TILES();
  tilesLoading = false;
  summaryParagraphs: string[] = [];
  summaryLoading = false;
  summaryFailed = false;

  readonly timeframeLabel = DD_TIMEFRAME_LABEL;

  @ViewChild(CompanyFormComponent) companyFormRef: CompanyFormComponent;

  private generationSub: Subscription | null = null;
  private destroy$ = new Subject<void>();
  // Cancels an in-flight results fetch when a new analysis starts or the user
  // returns to search — prevents a slow prior run overwriting newer tiles.
  private newRun$ = new Subject<void>();

  constructor(
    public config: ConfigService,
    private userInfo: UserInfoService,
    private aiSearchService: AiSearchService,
    private reportStateService: ReportStateService,
    private sentimentService: SentimentService,
    private subEventsService: SubEventsService,
    private volumeTimeSeriesService: VolumeTimeSeriesService,
    private ddSummaryService: DdSummaryService,
    private recentsService: DueDiligenceRecentsService,
    private entityInfoService: EntityInfoService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.userEmail = this.userInfo.email || '';
    this.project = this.config.appConfig.routing.project;
    this.recents = this.recentsService.list();
  }

  ngOnDestroy(): void {
    this.generationSub?.unsubscribe();
    this.newRun$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Search step ---

  startSearch(): void {
    this.errorMessage = '';
    this.companyFormRef?.onSearch();
  }

  onSearchStateChange(state: { enabled: boolean; enriching: boolean }): void {
    this.companySearchEnabled = state.enabled;
    if (state.enriching && this.state === 'search') {
      this.loadingPhase = 'enriching';
      this.loadingSubject = this.companyFormRef?.legalName || '';
      this.state = 'loading';
    }
  }

  onCompanySubmit(data: CompanyFormData): void {
    // A cancelled enrichment still emits when its request settles — the form
    // stays alive (CSS-hidden) while loading. Only act when still loading.
    if (this.state !== 'loading') return;

    this.loadingPhase = 'generating';
    this.loadingSubject = data.request.legal_name;
    // Kept so the results-page "Search parameters" panel can show the inputs.
    this.pendingRequest = data.request;
    this.generationSub = this.aiSearchService
      .generateCompanySearch(data)
      .subscribe({
        next: (response) => this.onGenerated(response),
        error: (err) => {
          this.state = 'search';
          this.errorMessage = this.generationError(
            err,
            'Failed to generate the analysis. Please try again.',
          );
        },
      });
  }

  onNavigateToEntity(entity: { type: string; id: string }): void {
    this.aiSearchService.navigateToEntity(entity);
  }

  cancelLoading(): void {
    this.generationSub?.unsubscribe();
    this.generationSub = null;
    this.state = 'search';
  }

  // --- Results ---

  openRecent(entry: RecentDdSearch): void {
    // Restore the stored request when present (older recents may not have it;
    // the generated query still renders from the stored query).
    this.request = entry.request ?? null;
    this.lexicon = entry.lexicon ?? null;
    this.aiSearchService.applySearchState(entry.data);
    this.showResults(entry.data);
  }

  clearRecents(): void {
    this.recentsService.clear();
    this.recents = [];
  }

  openFullResults(): void {
    if (!this.resultData) return;
    this.aiSearchService.launchAiSearch(this.resultData, {
      time: DD_TIMEFRAME,
    });
  }

  resetToSearch(): void {
    this.newRun$.next();
    this.resultData = null;
    this.tiles = EMPTY_DD_TILES();
    this.summaryParagraphs = [];
    this.summaryFailed = false;
    this.errorMessage = '';
    this.companySearchEnabled = false;
    this.paramsOpen = false;
    this.request = null;
    this.lexicon = null;
    this.editing = false;
    this.dQuery = null;
    this.draftModel = null;
    this.draftRequest = null;
    this.addKey = null;
    this.addDraft = '';
    this.rerunning = false;
    this.rerunAt = null;
    this.deltas = null;
    this.prevTiles = null;
    this.state = 'search';
  }

  // --- Search parameters panel ---

  toggleParams(): void {
    this.paramsOpen = !this.paramsOpen;
  }

  // Save the current search via the shared query-name dialog (same picker the
  // entity page uses): 'add' → existing collection, 'create' → new collection.
  onSave(action: 'add' | 'create'): void {
    if (!this.resultData) return;
    // Sync the global query state so the dialog saves this DD query.
    this.aiSearchService.applySearchState(this.resultData);
    this.dialog.open(QueryNameComponent, { data: { action } });
  }

  /** The tree the builder renders: the draft while editing, else the committed one. */
  get activeQuery(): YukkaQuery | null {
    return (this.editing ? this.dQuery : this.resultData?.query) ?? null;
  }

  // Header badges use the draft in edit mode, the committed query otherwise.
  // The zones' own model is the builder's business: the badges only need the two
  // counts, and deriving a second model here meant walking every tree twice.
  get queryBadges(): QbCounts {
    return this.editing && this.draftModel
      ? this.draftModel.counts
      : queryCounts(this.resultData?.query);
  }

  /** Every edit arrives as a whole new tree — the builder holds no state. */
  onQueryChange(tree: YukkaQuery): void {
    this.dQuery = tree;
    this.refreshDraft();
  }

  // --- Edit & re-run ---

  enterEdit(): void {
    if (!this.resultData?.query) return;
    this.paramsOpen = true;
    this.editing = true;
    this.addKey = null;
    this.addDraft = '';
    this.dQuery = cloneTree(this.resultData.query);
    this.refreshDraft();
    // Editable company parameters (only when we know the request — fresh search
    // or a recent that stored it). Editing these re-generates the query. A
    // shallow copy suffices: edits only set or delete top-level keys.
    this.draftRequest = this.request ? { ...this.request } : null;
    this.loadSectors();
  }

  private refreshDraft(): void {
    this.draftModel = this.dQuery
      ? deriveBuilderModel(this.dQuery, this.lexicon)
      : null;
  }

  // Reference sector list for the sector dropdown (fetched once, best-effort).
  private loadSectors(): void {
    if (this.sectorOptions.length) return;
    this.entityInfoService
      .getSectors()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.sectorOptions = (res.entities ?? [])
            .map((e) => e.name)
            .filter((name): name is string => !!name)
            .sort((a, b) => a.localeCompare(b));
        },
        error: () => {
          // Dropdown falls back to just the current value on failure.
        },
      });
  }

  cancelEdit(): void {
    this.editing = false;
    this.dQuery = null;
    this.draftModel = null;
    this.draftRequest = null;
    this.addKey = null;
    this.addDraft = '';
  }

  // Company fields the reviewer may edit. An emptied field is deleted, never
  // sent as '' — the API contract treats unknown values as absent keys.
  setDraftField(field: DdEditableField, value: string): void {
    const draft = this.draftRequest;
    if (!draft) return;
    const v = value.trim();
    if (v) draft[field] = v;
    else delete draft[field];
  }

  // --- List fields (key persons, exclusion terms): one pill per entry ---

  draftList(field: DdListField): string[] {
    return this.draftRequest?.[field] ?? [];
  }

  // Entries are plain strings and may repeat while being typed — track by index
  // so a duplicate doesn't collapse two pills into one.
  trackByIndex(index: number): number {
    return index;
  }

  // Emptying an entry removes it, and an emptied list drops the key entirely.
  setDraftListItem(field: DdListField, index: number, value: string): void {
    const list = [...this.draftList(field)];
    const v = value.trim();
    if (v) list[index] = v;
    else list.splice(index, 1);
    this.writeDraftList(field, list);
  }

  removeDraftListItem(field: DdListField, index: number): void {
    const list = [...this.draftList(field)];
    list.splice(index, 1);
    this.writeDraftList(field, list);
  }

  // Commits the add-picker input; the picker stays open for the next entry.
  commitDraftListAdd(field: DdListField): void {
    const value = this.addDraft.trim();
    if (!value) return;
    this.writeDraftList(field, [...this.draftList(field), value]);
    this.addDraft = '';
  }

  private writeDraftList(field: DdListField, list: string[]): void {
    const draft = this.draftRequest;
    if (!draft) return;
    if (list.length) draft[field] = list;
    else delete draft[field];
  }

  // "▲ +4" / "▼ -2" / "unchanged" for a stat-card delta.
  deltaLabel(value: number | null | undefined, suffix = ''): string {
    if (value == null) return '';
    if (value === 0) return 'unchanged';
    return (value > 0 ? '▲ +' : '▼ ') + value + suffix;
  }

  // --- Add-term pickers (one per row, plus one per ambiguous context line) ---

  isAddOpen(key: string): boolean {
    return this.addKey === key;
  }

  openAdd(key: string): void {
    this.addKey = this.addKey === key ? null : key;
    this.addDraft = '';
  }

  closeAdd(): void {
    this.addKey = null;
    this.addDraft = '';
  }

  // Fields DD accepts. Events are deliberately absent: the tiles measure the
  // event spread of whatever the query matches, so filtering by event would
  // make the page measure its own filter.
  readonly pickerFields: TermField[] = ['text', 'entity', 'source'];

  // Inline-input width tracks the term length (min 4ch). Used by the company
  // parameter pills; the query builder sizes its own.
  pillWidth(value: string): string {
    return `max(4ch, ${(value?.length ?? 0) + 1}ch)`;
  }

  // Restore the payload as issued: both the query tree and the company
  // parameters, so the footer diff really does go back to "no changes".
  resetToGenerated(): void {
    if (!this.resultData?.query) return;
    this.dQuery = cloneTree(this.resultData.query);
    this.draftRequest = this.request ? { ...this.request } : null;
    this.addKey = null;
    this.addDraft = '';
    this.refreshDraft();
  }

  // A grounded block with no term or no context blocks the re-run.
  get hasIncompleteGrounded(): boolean {
    return hasIncomplete(this.draftModel?.groups ?? []);
  }

  /**
   * The footer's single line. Editing a company parameter re-runs
   * generate/company, which replaces the query tree wholesale — so any term the
   * user curated by hand is discarded. Say so before they press the button.
   */
  get editNote(): string {
    if (this.hasIncompleteGrounded) {
      return 'Incomplete ambiguous form — add its term and at least one context term to re-run';
    }
    if (!this.changedKeys.length) {
      return 'No changes yet — adjust a parameter to re-run';
    }
    if (this.regenerateDiscardsQuery) {
      return `Changed: ${this.editSummary} · a company parameter changed, so the query is generated again — your query edits will be discarded`;
    }
    return `Changed: ${this.editSummary} · re-running replaces the current results`;
  }

  /** True when a re-run will regenerate the tree over the user's own edits. */
  get regenerateDiscardsQuery(): boolean {
    return !!this.paramChangedKeys().length && this.queryChanged;
  }

  // Which committed fields the draft has diverged from (drives the footer note).
  get changedKeys(): string[] {
    const keys: string[] = [...this.paramChangedKeys()];
    if (this.queryChanged) keys.push('query');
    return keys;
  }

  private get queryChanged(): boolean {
    if (!this.dQuery || !this.resultData?.query) return false;
    return (
      JSON.stringify(this.dQuery) !== JSON.stringify(this.resultData.query)
    );
  }

  // Human-readable diff for the footer: params by name, query by counts.
  get editSummary(): string {
    const parts: string[] = [...this.paramChangedKeys()];
    if (this.queryChanged) {
      const diff = this.queryDiff;
      parts.push(diff ? `query (${diff})` : 'query');
    }
    return parts.join(', ');
  }

  // Structural summary of the query change for the footer diff line.
  get queryDiff(): string {
    if (!this.dQuery || !this.resultData?.query) return '';
    const a = queryCounts(this.resultData.query);
    const b = queryCounts(this.dQuery);
    const parts: string[] = [];
    if (a.terms !== b.terms) parts.push(`terms ${a.terms}→${b.terms}`);
    if (a.excluded !== b.excluded)
      parts.push(`exclusions ${a.excluded}→${b.excluded}`);
    return parts.join(', ');
  }

  keyPersonLabel(persons?: string[] | null): string {
    return (persons?.length ?? 0) > 1 ? 'Key persons' : 'Key person';
  }

  // Company parameters that changed — these trigger a full re-generation. Any
  // request key is reported; the two whose field label differs are mapped so the
  // footer matches the form.
  private paramChangedKeys(): string[] {
    const draft = this.draftRequest;
    const current = this.request;
    if (!draft || !current) return [];
    const labels: Record<string, string> = {
      exclusion_seeds: 'exclusion terms',
      message: 'guidance',
    };
    const keys = new Set([...Object.keys(current), ...Object.keys(draft)]);
    return [...keys]
      .filter(
        (key) =>
          JSON.stringify(current[key as keyof CompanyQueryRequest]) !==
          JSON.stringify(draft[key as keyof CompanyQueryRequest]),
      )
      .map((key) => labels[key] ?? key.replace(/_/g, ' '));
  }

  rerun(): void {
    if (
      !this.dQuery ||
      !this.resultData ||
      this.rerunning ||
      !this.changedKeys.length
    ) {
      return;
    }
    // Guardrail: a bare broad term with no context floods the results — the
    // precise failure grounded forms exist to prevent. Refuse, no request.
    if (this.hasIncompleteGrounded) {
      this.snackBar.open(
        'Every ambiguous form needs a term and at least one context term',
        'OK',
        { duration: 4200 },
      );
      return;
    }
    // Snapshot tiles for the deltas before either re-run path.
    this.prevTiles = this.tiles;
    this.rerunAt = Date.now();
    this.rerunning = true;

    if (this.paramChangedKeys().length && this.draftRequest) {
      // Company inputs changed → re-run generate/company for a fresh query. The
      // draft is the previous request with the edited keys applied, so every
      // untouched field (identifiers, exchanges, surface forms) rides along.
      this.regenerate(this.draftRequest);
      return;
    }

    // Query-only change → commit the draft tree and re-fetch tiles + summary.
    this.resultData = { ...this.resultData, query: this.dQuery };
    this.editing = false;
    this.dQuery = null;
    this.draftModel = null;
    this.draftRequest = null;
    this.addKey = null;
    this.aiSearchService.applySearchState(this.resultData);
    this.fetchTiles();
  }

  // Re-generate the query from edited company parameters, then re-fetch.
  private regenerate(request: CompanyQueryRequest): void {
    this.editing = false;
    this.dQuery = null;
    this.draftModel = null;
    this.draftRequest = null;
    this.errorMessage = '';
    this.generationSub?.unsubscribe();
    this.generationSub = this.aiSearchService
      .generateCompanyQuery(request)
      .subscribe({
        next: (response) => {
          const data: AiGeneratedQueryData = {
            name: response.search_query.name,
            description: truncateDescription(response.search_query.description),
            query: response.search_query.query,
            groups: [],
            _isAiGenerated: true,
          };
          // Only adopt the edited request once it actually produced a query —
          // otherwise a failed re-run would leave the panel describing
          // parameters that were never applied, with nothing left to re-run.
          this.request = request;
          this.lexicon = response.lexicon ?? null;
          this.resultData = data;
          this.aiSearchService.applySearchState(data);
          this.reportStateService.checkStatuses({ mode: 'query' });
          this.fetchTiles();
          this.recents = this.recentsService.add(data, request, this.lexicon);
        },
        error: (err) => {
          this.rerunning = false;
          this.prevTiles = null;
          this.errorMessage = this.generationError(
            err,
            'Failed to re-generate the analysis. Please try again.',
          );
        },
      });
  }

  retrySummary(): void {
    this.fetchSummary();
  }

  private onGenerated(response: LexiconResponse): void {
    const data: AiGeneratedQueryData = {
      name: response.search_query.name,
      description: truncateDescription(response.search_query.description),
      query: response.search_query.query,
      groups: [],
      _isAiGenerated: true,
    };
    this.request = this.pendingRequest;
    this.lexicon = response.lexicon ?? null;
    this.aiSearchService.applySearchState(data);
    this.showResults(data);
    // Best-effort persistence, after the view is shown — never gates results.
    this.recents = this.recentsService.add(data, this.request, this.lexicon);
  }

  private showResults(data: AiGeneratedQueryData): void {
    this.resultData = data;
    // Fresh results start with no draft/delta history.
    this.editing = false;
    this.dQuery = null;
    this.draftModel = null;
    this.rerunAt = null;
    this.deltas = null;
    this.prevTiles = null;
    this.addKey = null;
    this.state = 'results';
    this.reportStateService.checkStatuses({ mode: 'query' });
    this.fetchTiles();
  }

  // A timed-out generation carries no API detail — say so rather than blaming
  // the request.
  private generationError(err: unknown, fallback: string): string {
    if ((err as { name?: string })?.name === 'TimeoutError') {
      return 'This is taking longer than expected. Please try again.';
    }
    return (err as { error?: { detail?: string } })?.error?.detail || fallback;
  }

  private fetchTiles(): void {
    if (!this.resultData) return;
    const payload = this.resultData.query;
    const iparams = { time: this.timeframe };

    this.newRun$.next();
    this.tiles = EMPTY_DD_TILES();
    this.tilesLoading = true;
    // Summary is pending from the moment tiles start (it fetches right after),
    // so one flag drives the whole loading span.
    this.summaryLoading = true;
    this.summaryFailed = false;
    this.summaryParagraphs = [];
    forkJoin({
      sentiment: this.sentimentService
        .getQuerySentiment(payload, iparams)
        .pipe(catchError(() => of(null))),
      events: this.subEventsService.fetchSubEventsListForQuery(
        payload,
        iparams,
      ),
      volume: this.volumeTimeSeriesService
        .getQueryVolumeTimeSeries(payload, iparams)
        .pipe(catchError(() => of(null))),
    })
      .pipe(takeUntil(merge(this.destroy$, this.newRun$)))
      .subscribe(({ sentiment, events, volume }) => {
        const volumeTs: { volume: number }[] = volume?.['volume_ts'] ?? [];
        const topEvents = events.events
          .slice()
          .sort((a, b) => b.count - a.count)
          .slice(0, DD_TOP_EVENTS_SHOWN);
        const maxCount = topEvents[0]?.count || 1;
        this.tiles = {
          sentiment: sentiment?.sentiment?.sentiment ?? null,
          eventsTotal: events.totalCount,
          topEvents: topEvents.map((row) => ({
            name: row.event.name,
            count: row.count,
            barWidth: `${Math.max(6, Math.round((row.count / maxCount) * 100))}%`,
          })),
          volumeTotal: volume
            ? volumeTs.reduce((sum, day) => sum + (day.volume || 0), 0)
            : null,
        };
        this.tilesLoading = false;
        if (this.prevTiles) {
          this.deltas = this.computeDeltas(this.prevTiles, this.tiles);
          this.prevTiles = null;
        }
        this.rerunning = false;
        this.fetchSummary();
      });
  }

  private computeDeltas(
    before: DdResultTiles,
    after: DdResultTiles,
  ): DdTileDeltas {
    const diff = (a: number | null, b: number | null): number | null =>
      a != null && b != null ? a - b : null;
    const pct = (v: number | null): number | null =>
      v != null ? Math.round(v * 100) : null;
    return {
      sentiment: diff(pct(after.sentiment), pct(before.sentiment)),
      events: diff(after.eventsTotal, before.eventsTotal),
      volume: diff(after.volumeTotal, before.volumeTotal),
    };
  }

  private fetchSummary(): void {
    if (!this.resultData) return;
    this.summaryParagraphs = [];
    this.summaryFailed = false;
    this.summaryLoading = true;
    this.ddSummaryService
      .generateSummary({
        companyName: this.resultData.name,
        timeframeLabel: this.timeframeLabel,
        sentimentPct:
          this.tiles.sentiment !== null ? this.tiles.sentiment * 100 : null,
        volumeTotal: this.tiles.volumeTotal,
        topEvents: this.tiles.topEvents.map((row) => ({
          name: row.name,
          count: row.count,
        })),
      })
      .pipe(takeUntil(merge(this.destroy$, this.newRun$)))
      .subscribe({
        next: (text) => {
          this.summaryParagraphs = text
            .split(/\n{1,2}/)
            .map((paragraph) => paragraph.trim())
            .filter((paragraph) => paragraph.length > 0);
          this.summaryLoading = false;
        },
        error: () => {
          this.summaryLoading = false;
          this.summaryFailed = true;
        },
      });
  }
}
